/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable */
import {
  Agreement,
  FieldSheet,
  FieldSheetResponseCustomer,
  FieldSheetSection1,
  FieldSheetSection10Club,
  FieldSheetSection11PaymentsLast3,
  FieldSheetSection12GrandTotalAllPastDue,   
  FieldSheetSection14Total,
  FieldSheetSection14WorkedHistory,
  FieldSheetSection15TotalCommitments,
  FieldSheetSection16CommitmentsSinceDueDate,
  FieldSheetSection2,
  FieldSheetSection3CustomerCredits,
  FieldSheetSection3NSFChargeback,
  FieldSheetSection4CustomerAlerts,
  FieldSheetSection5ProductBalances,
  FieldSheetSection6,
  FieldSheetSection6PastDueAgreements,
  FieldSheetSection7Inventory,
  FieldSheetSection8Payments,
  FieldSheetSection9Total,
  GetCustomerFieldSheetsPayload,
} from '../../types/types';
import {
  formatDate,
  formatDateString,
  formatMoney,
  formatPhoneNumber,
  formatStringDateHoursAndMinutes,
  getSelectedStore,
  orderByFieldAndValue,
} from '../../utils/utils';
import { getFieldSheets } from '../../api/user';
import { printLetterOptions } from '../../constants/constants';
import i18n from '../../i18n';


const mapSection1 = (customer: FieldSheetResponseCustomer, storeName: string, storeNumber: string): FieldSheetSection1 => {
  const customerAddress = customer.addresses?.find((address) => address.addressTypeCode === 'PRIM');
  const deliveryAddress = `${customerAddress?.addressLine1 || ''} ${customerAddress?.city || ''} ${customerAddress?.state || ''} ${customerAddress?.postalCode || ''}`;
  const store = `${storeName || ''} ${storeNumber || ''}`;
  return { route: customer.route.description, store, delivery: deliveryAddress, date: formatDate(new Date()) };
};

const mapSection2 = (customer: FieldSheetResponseCustomer): FieldSheetSection2 => {
  const customerSinceDateFormatted = customer.customerSinceDate ? formatDateString(customer.customerSinceDate) : '';
  let customerSinceString = customerSinceDateFormatted ? `${i18n.t('Customer since:')} ${customerSinceDateFormatted}` : '';


  const primaryAddress = customer.addresses?.find((address) => address.addressTypeCode === 'PRIM') || null;
  let customerAddress = '';
  if (primaryAddress) {
    customerAddress = `${primaryAddress.addressLine1 || ''} ${primaryAddress.addressLine2 || ''} \n ${
      primaryAddress.city || ''} ${primaryAddress.state || ''} \n ${primaryAddress.postalCode || ''}`;
  }
  const customerColumn = `${customerSinceString}\n${customer.firstName || ''} ${customer.lastName || ''}\n ${customerAddress}`;

  const activeEmployer = customer.employerReferences?.find((employer) => employer.active && employer.active !== 'N') || null;
  let workColumn = '';
  if (activeEmployer) {
    workColumn = `${activeEmployer.employerName || ''}\n${(activeEmployer.addressLine1 || '') + (activeEmployer.addressLine2 || '')}\n${
      activeEmployer.city || ''}, ${activeEmployer.state || ''}\n${formatPhoneNumber(activeEmployer.employerPhoneNumber || '')}\nShift: ${
      activeEmployer.workStartTime || ''} - ${activeEmployer.workEndTime || ''}\n`;
  }
  const phoneData = orderByFieldAndValue('Y', 'primary', customer.phones || []) || [];
  const phoneColumn = phoneData.length > 0 ? phoneData.map(phone => `${formatPhoneNumber(phone.phoneNumber || '')}\n${
        phone.callTimeTypeDesc ? i18n.t('Best time to call') + ':\n ' + i18n.t('Call in ') + i18n.t(phone.callTimeTypeDesc || '') : ''
      }\n\n`).join(' ') : '';
  return { customerColumn, workColumn, vehicleColumn: '', phoneColumn };
};

const mapSection2Corenter = (customer: FieldSheetResponseCustomer): FieldSheetSection2 => {
  const { corenter } = customer;
  if (!corenter) return { customerColumn: '', workColumn: '', vehicleColumn: '', phoneColumn: '' };
  const customerSinceDateFormatted = formatDateString(corenter.customerSinceDate);
  let customerSinceString = customerSinceDateFormatted ? `${i18n.t('Customer since:')} ${customerSinceDateFormatted}` : '';
  const customerColumn = `${customerSinceString}\n${corenter.firstName || ''} ${corenter.lastName || ''}`;
  const phoneData = orderByFieldAndValue('Y', 'primary', corenter.phones) || [];
  const phoneColumn = phoneData.length > 0 ? phoneData.map(phone => `${formatPhoneNumber(phone.phoneNumber)}\n${phone.callTimeTypeDesc ? i18n.t('Best time to call') + ':\n ' + i18n.t('Call in ') + i18n.t(phone.callTimeTypeDesc) : ''}\n\n`).join(' ') : '';
  return { customerColumn, workColumn: '', vehicleColumn: '', phoneColumn };
};

const mapSection3 = (customer: FieldSheetResponseCustomer): FieldSheetSection3NSFChargeback => {
  const { nsfTotalAmount = 0, nsfTotalNumber = 0, achTotalAmount = 0, achTotalNumber = 0, chargeBackTotalAmount = 0, chargeBackTotalNumber = 0 } = customer.nsfAndChargebackInfo || {};
  return { nsfTotalAmount: formatMoney(nsfTotalAmount), nsfTotalNumber: nsfTotalNumber.toString(), achTotalAmount: formatMoney(achTotalAmount), achTotalNumber: achTotalNumber.toString(), chargebackTotalAmount: formatMoney(chargeBackTotalAmount), chargebackTotalNumber: chargeBackTotalNumber.toString() };
};

const mapSection3CustomerCredit = (customer: FieldSheetResponseCustomer): FieldSheetSection3CustomerCredits => {
  const { suspense = 0, promo = 0, sips = 0, promoFreeDays = 0, coa = 0, total = 0 } = customer?.customerCreditInfo || {};
  return { suspense: formatMoney(suspense), promo: formatMoney(promo), sips: formatMoney(sips), promoFreeDays: promoFreeDays.toString(), coa: formatMoney(coa), total: formatMoney(total) };
};

const mapSection4Alerts = (customer: FieldSheetResponseCustomer): FieldSheetSection4CustomerAlerts[] => (customer.alerts?.filter((alert) => alert.alertCleared !== '1') || []).map(alert => ({ alertTime: formatDateString(alert.alertTime) || '', alertText: alert.alertText || alert.alertType || '' }));

const mapSection5ProductBalances = (customer: FieldSheetResponseCustomer): FieldSheetSection5ProductBalances[] => (customer?.productBalances?.map(balance => {
  const remainingValueNumber = parseFloat(balance.remainingValue);
  if (!isNaN(remainingValueNumber) && remainingValueNumber < 0) {
    console.warn(`Negative remainingValue for agreement ${balance.agreementNumber}: ${balance.remainingValue}`);
  }
  return {
    agreementNumber: balance.agreementNumber,
    epo: formatMoney(balance.epo),
    paidIn: formatMoney(balance.paidIn),
    remainingValue: formatMoney(balance.remainingValue),
  };
}) || []);

const mapSection6PastDueAgreements = (agreement: Agreement): FieldSheetSection6PastDueAgreements => ({
  agreementNumber: agreement.agreementNumber || '',
  date: formatDateString(agreement.openDate),
  pastDue1to6: agreement.pastDue1to6 || '',
  pastDue7to14: agreement.pastDue7to14 || '',
  pastDue15Plus: agreement.pastDue15Plus || '',
  nextDueDate: formatDateString(agreement.nextDueDate),
  pastDue: agreement.pastDue || '',
  agreementRetention: [], 
});

const mapSection7Inventory = (agreement: Agreement): FieldSheetSection7Inventory[] => (agreement?.inventory?.map(inv => ({ ...inv })) || []);

const mapSection8Payments = (agreement: Agreement): FieldSheetSection8Payments[] => (agreement?.payments?.map(payment => ({
  paidOn: formatDateString(payment.paidOn) || '',
  receipt: payment.receiptNumber || '',
  rental: formatMoney(payment.rental) || '',
  tax: formatMoney(payment.tax) || '',
  dueDate: formatDateString(payment.dueDate) || '',
  freeDays: payment.freeDays || '0',
  daysLate: payment.daysLate || '0',
  type: payment.paymentOrigin || '',
  tendered: payment.paymentMethod || '',
  payOffDate: payment.payoffDate || '',
})) || []);

const mapSection9Total = (agreement: Agreement): FieldSheetSection9Total => ({
  carriedLate: formatMoney(agreement.totals?.carriedLate),
  carriedRent: formatMoney(agreement.totals?.carriedRent),
  deferredRent: formatMoney(agreement.totals?.deferredRent),
  lateFees: formatMoney(agreement.totals?.lateFees),
  ldw: formatMoney(agreement.totals?.ldw),
  otherFees: formatMoney(agreement.totals?.otherFees),
  rent: formatMoney(agreement.totals?.rent),
  tax: formatMoney(agreement.totals?.tax),
  total: formatMoney(agreement.totals?.total),
});

const mapSection6 = async (customer: FieldSheetResponseCustomer): Promise<FieldSheetSection6[]> => {
  return (customer?.pastDueAgreementInfo?.map(agreement => ({
    section6PastDueAgreements: mapSection6PastDueAgreements(agreement),
    section6Inventory: mapSection7Inventory(agreement),
    section6Payments: mapSection8Payments(agreement),
    section6Total: mapSection9Total(agreement),
  })) || []);
};

const mapSection10Club = (customer: FieldSheetResponseCustomer): FieldSheetSection10Club => ({
  activeSince: formatDateString(customer.pastDueClubInfo?.activeSince) || '',
  club: customer.pastDueClubInfo?.clubName || '',
  dueDate: formatDateString(customer.pastDueClubInfo?.dueDate) || '',
  pastDue: customer.pastDueClubInfo?.pastDue || '',
});

const mapSection11Payments = (customer: FieldSheetResponseCustomer): FieldSheetSection11PaymentsLast3[] => (customer?.pastDueClubInfo?.payments?.map(payment => ({
  amountPaid: formatMoney(payment.amountPaid),
  dueDate: formatDateString(payment.dueDate) || '',
  paidOn: formatDateString(payment.paidOn) || '',
  receipt: payment.receiptNumber || '',
  tendered: payment.paymentMethod || '',
})) || []);

const mapSection12GrandTotal = (customer: FieldSheetResponseCustomer): FieldSheetSection12GrandTotalAllPastDue => ({
  carriedLate: formatMoney(customer?.grandTotalsAllPastDue?.carriedLate),
  carriedRent: formatMoney(customer?.grandTotalsAllPastDue?.carriedRent),
  deferredRent: formatMoney(customer?.grandTotalsAllPastDue?.deferredRent),
  lateFees: formatMoney(customer?.grandTotalsAllPastDue?.lateFees),
  ldw: formatMoney(customer?.grandTotalsAllPastDue?.ldw),
  otherFees: formatMoney(customer?.grandTotalsAllPastDue?.otherFees),
  rent: formatMoney(customer?.grandTotalsAllPastDue?.rent),
  tax: formatMoney(customer?.grandTotalsAllPastDue?.tax),
  total: formatMoney(customer?.grandTotalsAllPastDue?.total),
  club: formatMoney(customer?.grandTotalsAllPastDue?.club),
});

const mapSection14TotalWorkedHistory = (customer: FieldSheetResponseCustomer): FieldSheetSection14Total => {
  const workedHistoryLabel = printLetterOptions.find(option => option.value === customer?.workedHistoryInfo?.workedHistoryDays)?.label || '';
  return { totalWorkedHistoryDays: customer?.workedHistoryInfo?.totalWorkedHistoryDays || '0', workedHistoryDays: workedHistoryLabel };
};

const mapSection14WorkedHistory = (customer: FieldSheetResponseCustomer): FieldSheetSection14WorkedHistory[] => (customer.workedHistoryInfo?.workedItems?.map(item => ({
  date: formatDateString(item.workedDate),
  workedResult: item.callResult || '',
  employee: item.coWorker || '',
  contactName: item.contact || '',
  phoneDialed: item.phoneNumber || '',
  relation: item.relation || '',
})) || []);

const mapSection15TotalCommitments = (customer: FieldSheetResponseCustomer): FieldSheetSection15TotalCommitments => ({
  broken: customer?.commitmentInfo?.totals?.broken || '0',
  brokenPaid: customer?.commitmentInfo?.totals?.brokenPaid || '0',
  honored: customer?.commitmentInfo?.totals?.honored || '0',
  open: customer?.commitmentInfo?.totals?.open || '0',
  revised: customer?.commitmentInfo?.totals?.revised || '0',
  total: customer?.commitmentInfo?.totals?.total || '0',
});

const mapSection16Commitments = (customer: FieldSheetResponseCustomer): FieldSheetSection16CommitmentsSinceDueDate[] => (customer?.commitmentInfo?.commitmentsSinceDueDate?.map(commitment => ({
  date: formatDateString(commitment.commitmentDate),
  time: formatStringDateHoursAndMinutes(commitment.commitmentDate),
  status: commitment.description || '',
  amount: formatMoney(commitment.amount),
  memo: commitment.memo || '',
})) || []);

export function onFetchFieldSheets(days: string, customerIds: string[], createFieldVisitEvent: boolean) {
  let fieldSheets: FieldSheet[] = [];
  let isLoading = false;
  let hasApiError = false;

  return new Promise<{ fieldSheets: FieldSheet[]; isLoading: boolean; hasApiError: boolean }>(async (resolve, reject) => {
    try {
    
      fieldSheets = [];
      isLoading = true;
      hasApiError = false;
      const payload: GetCustomerFieldSheetsPayload = { storeNumber: getSelectedStore(), workedHistoryType: days, customerIds };
       
      const response: any = await getFieldSheets(payload);
       
      const storeName = response?.data?.customersFieldSheet?.storeName || '';
      const storeNumber = response?.data?.customersFieldSheet?.storeNumber || '';
      const fieldSheetsData = response?.data?.customersFieldSheet?.fieldSheets[0]?.customer || [];

      fieldSheetsData.sort((a, b) => customerIds.indexOf(a.customerId) - customerIds.indexOf(b.customerId));
      const mappedFieldSheets = fieldSheetsData.map(async (sheet: FieldSheetResponseCustomer) => ({
        section1: mapSection1(sheet, storeName, storeNumber),
        section2: mapSection2(sheet),
        section2Corenter: mapSection2Corenter(sheet),
        section3NSFChargeback: mapSection3(sheet),
        section3CustomerCredits: mapSection3CustomerCredit(sheet),
        section4CustomerAlerts: mapSection4Alerts(sheet),
        section5ProductBalances: mapSection5ProductBalances(sheet),
        section6: await mapSection6(sheet),
        section10Club: mapSection10Club(sheet),
        section11PaymentsLast3: mapSection11Payments(sheet),
        section12GrandTotal: mapSection12GrandTotal(sheet),
        section14Total: mapSection14TotalWorkedHistory(sheet),
        section14WorkedHistory: mapSection14WorkedHistory(sheet),
        section15TotalCommitments: mapSection15TotalCommitments(sheet),
        section16CommitmentsSinceDueDate: mapSection16Commitments(sheet),
      }));

      fieldSheets = await Promise.all(mappedFieldSheets);
      isLoading = false;
      resolve({ fieldSheets, isLoading, hasApiError });
    } catch (error) {
      hasApiError = true;
      isLoading = false;
      reject(error);
    }
  });
}